
import type { InspectionItem, PrintOptions, SafetyPoint } from '../../types';
import { pinsAufPlan } from '../planPins';
import { NADEL_GRUNDNEIGUNG, NADEL_MAX_NEIGUNG } from '../planNadelNeigung';

export const TRANSLATIONS: Record<string, Record<string, string>> = {
  reportTitle: { de: 'Begehungsprotokoll', en: 'Site Inspection Report' },
  reportNr: { de: 'Nr.', en: 'No.' },
  projectInfo: { de: 'Projektinformationen', en: 'Project Information' },
  projectName: { de: 'Projekt', en: 'Project' },
  address: { de: 'Standort', en: 'Location' },
  dateTime: { de: 'Datum', en: 'Date' },
  time: { de: 'Uhrzeit', en: 'Time' },
  projectNr: { de: 'Projekt Nr.', en: 'Project No.' },
  responsibilities: { de: 'Verantwortlichkeiten', en: 'Responsibilities' },
  client: { de: 'Bauherr', en: 'Client' },
  customer: { de: 'Auftraggeber', en: 'Customer' },
  siteContact: { de: 'Ansprechpartner\nvor Ort', en: 'Site Contact' }, // Modified with newline
  sigeko: { de: 'SiGeKo', en: 'H&S Coordinator' },
  deputy: { de: 'Stellvertreter', en: 'Deputy' },
  // Zweizeilig: die Labelspalte auf dem Deckblatt ist 35 mm breit.
  conductedBy: { de: 'Begehung\ndurchgeführt durch', en: 'Inspection\nconducted by' },
  siteContacts: { de: 'Ansprechpartner vor Ort (Firma)', en: 'Site Contacts (Company)' },
  colCompany: { de: 'Firma', en: 'Company' },
  colName: { de: 'Name', en: 'Name' },
  distribution: { de: 'Verteiler', en: 'Distribution List' },
  colRole: { de: 'Rolle', en: 'Role' },
  colEmail: { de: 'E-Mail', en: 'Email' },
  colPresent: { de: 'Teilgenommen', en: 'Present' },
  colDist: { de: 'Verteiler', en: 'Dist.' },
  checkpoints: { de: 'Allgemeine Prüfpunkte', en: 'Checkpoints' },
  statusOK: { de: 'i.O.', en: 'OK' },
  statusNOK: { de: 'n.i.O.', en: 'NOK' },
  colCategory: { de: 'Kategorie', en: 'Category' },
  colPoint: { de: 'Prüfpunkt', en: 'Checkpoint' },
  colStatus: { de: 'Status', en: 'Status' },
  colDateCheck: { de: 'Geprüft am', en: 'Checked on' },
  colComment: { de: 'Bemerkung', en: 'Comment' },
  analysisTitle: { de: 'Statistik & Analyse', en: 'Statistics & Analysis' },
  statusDistribution: { de: 'Status Verteilung', en: 'Status Distribution' },
  progressRate: { de: 'Quote', en: 'Rate' },
  colTotal: { de: 'Gesamt', en: 'Total' },
  colOpen: { de: 'Offen', en: 'Open' },
  colDone: { de: 'Erledigt', en: 'Done' },
  colPositive: { de: 'Positiv', en: 'Positive' },
  colQuote: { de: 'Quote', en: 'Rate' },
  hazardsMap: { de: 'Gefährdungs-Matrix', en: 'Hazard Matrix' },
  legend: { de: 'Legende', en: 'Legend' },
  riskHigh: { de: 'Hoch', en: 'High' },
  riskMedium: { de: 'Mittel', en: 'Medium' },
  riskLow: { de: 'Gering', en: 'Low' },
  hazardsTable: { de: 'Gefährdungs-Tabelle', en: 'Hazards Table' },
  hazardSource: { de: 'Verursacher', en: 'Source' },
  hazardAffected: { de: 'Betroffener', en: 'Affected' },
  hazardMeasure: { de: 'Maßnahme', en: 'Measure' },
  hazardDescription: { de: 'Gefährdung', en: 'Hazard' },
  hazardRisk: { de: 'Risiko', en: 'Risk' },
  statusOpen: { de: 'Offen', en: 'Open' },
  statusInfo: { de: 'Hinweis', en: 'Info' },
  statusGood: { de: 'Positiv', en: 'Positive' },
  statusGeneral: { de: 'Information', en: 'Information' },
  statusClosed: { de: 'Erledigt', en: 'Closed' },
  clearance: { de: 'Freimeldung', en: 'Clearance' },
  responsible: { de: 'Zuständig', en: 'Responsible' },
  dateFound: { de: 'Festgestellt', en: 'Found on' },
  deadline: { de: 'Frist', en: 'Deadline' },
  immediateAction: { de: 'Sofortmaßnahme', en: 'Immediate action' },
  immediateActionNote: { de: 'Verantwortliche wurden informiert', en: 'responsible parties informed' },
  findings: { de: 'Feststellungen & Mängel', en: 'Findings & Defects' },
  preliminaryNotes: { de: 'Vorbemerkungen', en: 'Preliminary notes' },
  planLocation: { de: 'Verortung', en: 'Location on Plan' },
  historyTitle: { de: 'Erledigte Mängel (Historie)', en: 'Closed Items (History)' },
  colTitle: { de: 'Titel / Mangel', en: 'Title / Defect' },
  colDateClearance: { de: 'Erledigt am', en: 'Closed on' },
  pageLabel: { de: 'Seite', en: 'Page' },
  // Der Kasten mit QR-Code vor den Feststellungen: Empfaenger des PDFs melden
  // einen Mangel ohne Login als behoben (docs/freimeldung-online-plan.md).
  freimeldungTitle: { de: 'Mangel online als behoben melden', en: 'Report a defect as fixed online' },
  freimeldungHint: {
    de: 'QR-Code scannen oder Link öffnen, eigene Firma wählen, Mangel als behoben melden (Foto optional) oder mit Begründung zurückweisen. Die Prüfung erfolgt durch den Koordinator.',
    en: 'Scan the QR code or open the link, select your company, report the defect as fixed (photo optional) or reject it with a reason. The coordinator will verify.'
  },
  // Dieselbe Anleitung fuer Projekte mit Foto-Pflicht
  // (`metadata.freimeldung.fotoPflicht`) — sonst verspricht das PDF ein
  // freiwilliges Foto, und das Formular weist die Meldung danach ab.
  freimeldungHintFotoPflicht: {
    de: 'QR-Code scannen oder Link öffnen, eigene Firma wählen, Mangel als behoben melden (Foto als Nachweis Pflicht) oder mit Begründung zurückweisen. Die Prüfung erfolgt durch den Koordinator.',
    en: 'Scan the QR code or open the link, select your company, report the defect as fixed (photo as proof required) or reject it with a reason. The coordinator will verify.'
  },
  // Protokoll-Design Oktober 2026 (Kopf als Raster, Maengel nach Dringlichkeit)
  inspectedBy: { de: 'Begehung durch', en: 'Inspected by' },
  participantsTitle: { de: 'Teilnehmer und Verteiler', en: 'Participants and distribution' },
  colPresentShort: { de: 'Anw.', en: 'Pres.' },
  colDistShort: { de: 'Vert.', en: 'Dist.' },
  colLocation: { de: 'Ort', en: 'Location' },
  statistics: { de: 'Statistik', en: 'Statistics' },
  completionRate: { de: 'Erledigungsquote', en: 'Completion rate' },
  byCompany: { de: 'Nach Firma', en: 'By company' },
  excludingNotes: { de: 'ohne Hinweise und Informationen', en: 'excluding notes and information' },
  groupUrgent: { de: 'Sofort handeln', en: 'Act now' },
  groupOpen: { de: 'Offene Mängel', en: 'Open defects' },
  groupNotes: { de: 'Hinweise und positive Feststellungen', en: 'Notes and positive findings' },
  overdue: { de: 'überschritten', en: 'overdue' },
  photo: { de: 'Foto', en: 'Photo' },
  clearanceProof: { de: 'Beweisbild Erledigung', en: 'Clearance proof photo' },
  clearanceOn: { de: 'Freimeldung am', en: 'Cleared on' },
  closedWithProof: { de: 'Erledigt mit Bildnachweis', en: 'Closed with photo proof' }
};

export const COLORS = {
  text: { primary: [15, 23, 42], secondary: [100, 116, 139], light: [148, 163, 184] },
  bg: { page: [255, 255, 255], card: [255, 255, 255], header: [255, 255, 255] },
  brand: { primary: [37, 99, 235], light: [219, 234, 254] },
  section: { text: [30, 64, 175], line: [226, 232, 240] },
  status: {
      red: { bg: [254, 242, 242], text: [220, 38, 38], border: [254, 202, 202] },
      yellow: { bg: [254, 252, 232], text: [202, 138, 4], border: [254, 240, 138] },
      green: { bg: [240, 253, 244], text: [22, 163, 74], border: [187, 247, 208] },
      emerald: { bg: [236, 253, 245], text: [5, 150, 105], border: [167, 243, 208] },
      blue: { bg: [239, 246, 255], text: [37, 99, 235], border: [191, 219, 254] }
  }
};

// --- Auswertung der Begehungseinträge ---------------------------------------
// Pure Logik ohne jsPDF/DOM: PDF-Statistik (generatorStandard) und Bildschirm-
// Auswertung (AnalysisView) stehen im Alltag nebeneinander und müssen exakt
// dieselben Zahlen zeigen. Deshalb liegt die Zählweise hier an EINER Stelle.

/** Nur die Statuswerte, die für Sortierung und Statistik zählen. */
type StatusOnly = Pick<InspectionItem, 'status'>;

/** Was Sortierung und Statistik von einem Eintrag brauchen. */
type SortableItem = StatusOnly & Pick<InspectionItem, 'ticketNumber'>;

/**
 * Die Ausgabereihenfolge der Begehungseinträge — PDF und Bildschirmliste
 * teilen sich diesen Vergleich, damit Ansicht und Ausdruck nicht auseinander
 * laufen.
 *
 * „Information" steht IMMER ganz oben — es ist eine Vorbemerkung an den Leser
 * (z.B. eine Baustellenregel) und keine Feststellung, die man zwischen den
 * Mängeln suchen sollte.
 *
 * Innerhalb der Information-Gruppe wird AUFSTEIGEND sortiert: mehrere
 * Vorbemerkungen liest man in der Reihenfolge, in der sie erfasst wurden.
 * Alle übrigen Einträge (Mangel/Hinweis/Positiv) bleiben wie bisher
 * absteigend — dort steht der jüngste Fund oben.
 *
 * Einträge ohne Ticketnummer zählen als 0: bei den Informationen stehen sie
 * damit vorn, bei den Mängeln hinten.
 *
 * Bei den Informationen ist das ab jetzt der REGELFALL, nicht der Ausnahmefall:
 * eine Information trägt gar keine laufende Nummer mehr (siehe
 * `inspectionItemStatus.ts`). Damit stehen neue Vorbemerkungen vor den
 * Altbestand-Informationen, die ihre Nummer aus der Zeit davor noch tragen, und
 * mehrere nummernlose Vorbemerkungen behalten untereinander die Reihenfolge des
 * Eingabe-Arrays — `Array.prototype.sort` ist stabil, und die Berichtsliste ist
 * die einzige verbliebene Quelle ihrer Erfassungsreihenfolge.
 *
 * Ein Mangel ohne Nummer bleibt der Ausnahmefall (halb angelegter Eintrag) und
 * steht wie bisher hinten.
 */
export const compareItemsForReportOrder = (a: SortableItem, b: SortableItem): number => {
  const rankA = a.status === 'general' ? 0 : 1;
  const rankB = b.status === 'general' ? 0 : 1;
  if (rankA !== rankB) return rankA - rankB;
  const nrA = a.ticketNumber || 0;
  const nrB = b.ticketNumber || 0;
  return rankA === 0 ? nrA - nrB : nrB - nrA;
};

/**
 * Die aktiven Einträge eines Berichts in Ausgabereihenfolge.
 *
 * Filter und Sortierung sind getrennt: die Bildschirmliste braucht einen
 * anderen Filter (sie zeigt auch „Erledigt"), aber exakt dieselbe Reihenfolge
 * und benutzt deshalb direkt `compareItemsForReportOrder`.
 */
export const selectActiveItemsForReport = <T extends SortableItem>(items: T[]): T[] =>
  items
    .filter(i => i.status === 'open' || i.status === 'info' || i.status === 'good' || i.status === 'general')
    .sort(compareItemsForReportOrder);

/**
 * Grundmenge der Firmen-Tabelle (PDF-Statistik und AnalysisView).
 *
 * REGEL: Mitteilungen zählen nicht in die Firmenquote. „Hinweis" ('info') und
 * „Information" ('general') fliegen beide heraus.
 *
 * Beide tragen gelegentlich einen Firmennamen, sind aber nichts zu
 * Erledigendes: sie erhöhten in der Tabelle nur `total`, ohne je in
 * open/closed/good zu landen, und drückten damit die Quote der Zeile. Eine
 * Firma mit 1 erledigtem Mangel und 1 Hinweis stand so mit 50 % da, obwohl sie
 * alles abgearbeitet hatte — eine Quote, die sie durch Arbeit nie auf 100 %
 * hätte bringen können. Dieselbe Zählweise wie `actionableTotal` in
 * `countInspectionStats`: abzuarbeiten ist nur, was einen Auftrag enthält.
 *
 * RÜCKWIRKEND — SO GEWOLLT: Wird ein bereits versendeter Bericht neu gedruckt,
 * zeigt seine Firmentabelle jetzt andere (höhere) Quoten als das damals
 * verschickte PDF. Der Betreiber hat dem ausdrücklich zugestimmt: die alten
 * Prozente waren falsch, und zwei Zählweisen parallel zu pflegen wäre teurer
 * als die Abweichung. Die Einträge selbst bleiben unverändert, nur ihre
 * Statistikzeile ändert sich.
 *
 * RANDFALL — Firma NUR mit Mitteilungen: Sie verschwindet ganz aus der
 * Tabelle, statt mit „1 / 0 / 0 / 0 / 0 %" dazustehen. Das ist gewollt: eine
 * Nullquote liest sich wie „hat nichts abgearbeitet" und beschuldigt eine
 * Firma, die gar keinen Mangel hat. Verloren geht dabei nichts — der Hinweis
 * steht mitsamt Firmennamen weiterhin in den Feststellungen, und die
 * Statusverteilung oben zählt ihn eigens. Die Tabelle beantwortet nur die
 * Frage „wer hat wie viel offen?", und darauf lautet die Antwort hier: nichts.
 *
 * Nicht verwechseln mit `collectCompanyUsage` (companyMergeLogic): dort zählen
 * 'general' und 'info' bewusst mit, weil es um vorkommende Firmen-Schreibweisen
 * fürs Zusammenführen geht und nicht um Quoten.
 */
export const selectItemsForCompanyStats = <T extends StatusOnly>(items: T[]): T[] =>
  items.filter(i => i.status !== 'info' && i.status !== 'general');

/** Die Statuswerte, die auf einem Plan als Punkt erscheinen dürfen. */
const PLANNABLE_STATUSES: ReadonlySet<InspectionItem['status']> = new Set(['open', 'info', 'good']);

/**
 * Die Einträge, die auf einem Plan als Punkt gezeichnet werden.
 *
 * Maßgeblich ist der STATUS, nicht das bloße Vorhandensein der Koordinaten.
 * Beim Statuswechsel bleiben `planId`/`planCoordinates` bewusst stehen (die
 * Felder gehen verlustfrei nicht verloren, falls der Eintrag zurückgestellt
 * wird) — ohne Statusfilter zeichnete der Plananhang deshalb weiter einen
 * roten Punkt für etwas, das die Bildschirm-Planansicht längst nicht mehr
 * zeigt.
 *
 * Draußen bleiben:
 * - „Information": eine Vorbemerkung an den Leser hat fachlich keine Verortung.
 * - „Erledigt": abgearbeitet, steht in der Historie; ein roter Gefahrenpunkt
 *   dafür führt den Leser in die Irre, und die Historie nennt keinen Ort.
 *
 * SafetyPointsView (Bildschirm) und der PDF-Plananhang benutzen ausschließlich
 * diese Funktion, damit Ansicht und Ausdruck nicht wieder auseinanderlaufen.
 *
 * Seit 28.09.2026 kann ein Eintrag Nadeln auf mehreren Plänen tragen: er
 * gehört auf jeden Plan, auf dem mindestens eine seiner Nadeln steht
 * (services/planPins.ts). Welche Nadeln das sind, sagt `pinsAufPlan`.
 */
export const selectItemsOnPlan = <T extends StatusOnly & Pick<InspectionItem, 'planId' | 'planCoordinates'>>(
  items: T[],
  planId: string | null | undefined
): T[] =>
  items.filter(i => PLANNABLE_STATUSES.has(i.status) && pinsAufPlan(i, planId).length > 0);

// --- Erledigte Mängel im Standard-Bericht: Karte oder Tabellenzeile -----------

/**
 * Druckoptionen, wie der Standard-Bericht sie liest: `PrintOptions` plus der
 * Schalter `closedImagesOnlySinceLastReport` (siehe
 * `erledigteBilderNurSeitVorbericht`). Er steht bewusst hier und nicht in
 * `types.ts`, weil nur der Druckdialog und der Generator ihn kennen.
 */
export type BerichtDruckOptionen = PrintOptions & {
  closedImagesOnlySinceLastReport?: boolean;
};

// Tagesvergleich: Archiv und Mangel tragen mal ein reines Datum, mal einen
// Zeitstempel. Ein am Tag des Vorberichts Erledigter stand dort bereits mit
// Karte, gilt hier also als „älter".
const kalendertag = (s: string) => (/^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s);

/** Ab diesem Begehungsdatum gilt „ältere Erledigte ohne Bilder" als Vorgabe (Betreiber-Entscheid 30.09.2026). */
export const ERLEDIGTE_OHNE_BILDER_STICHTAG = '2026-09-30';

/**
 * Bekommen erledigte Mängel nur dann eine Karte mit Bildern, wenn sie SEIT
 * dem Vorbericht erledigt wurden? Ältere Erledigte stehen dann nur noch als
 * Tabellenzeile ohne Bild in der Historie.
 *
 * Betreiber-Entscheid 30.09.2026: das ist ab jetzt die VORGABE FÜR ALLE
 * PROJEKTE, abschaltbar je Projekt im Druckdialog. Anlass: 6051 druckte mit
 * jedem Bericht alle ~600 erledigten Mängel seit Baubeginn samt Beweisbild,
 * Vorschau und Versand dauerten entsprechend.
 *
 * WARUM EIN NEUER SCHLÜSSEL und nicht die Vorgabe des alten
 * `hideClosedImagesAfterNextReport` umdrehen: Der Druckdialog schreibt beim
 * Speichern das GANZE Optionsobjekt zurück. Ein gespeichertes `false` im
 * alten Schlüssel kann deshalb „bewusst abgewählt" ODER „einmal an- und
 * wieder ausgeklickt" heißen — unterscheiden lässt sich das nicht mehr. Die
 * neue Vorgabe soll aber ausnahmslos für alle bestehenden Projekte gelten.
 * Also zählt nur noch `closedImagesOnlySinceLastReport`: fehlt er, gilt AN;
 * nur ein ausdrückliches `false` (gesetzt über den neuen Schalter) schaltet
 * ab. Der alte Schlüssel zählt nur noch für Archivberichte vor dem Stichtag.
 *
 * AUSNAHME ARCHIV: Ein Bericht mit Begehungsdatum VOR dem Stichtag wurde nach
 * der alten Regel versendet. Wird sein Schnappschuss aus dem Archiv neu
 * gedruckt, muss er aussehen wie damals — für ihn gilt ohne neuen Schlüssel
 * weiter der alte (`hideClosedImagesAfterNextReport === true`). Ohne
 * Berichtsdatum (Druckdialog) gilt die neue Vorgabe.
 */
export const erledigteBilderNurSeitVorbericht = (
  optionen?: Partial<PrintOptions> | null,
  berichtsDatum?: string | null,
): boolean => {
  const o = optionen as Partial<BerichtDruckOptionen> | null | undefined;
  if (typeof o?.closedImagesOnlySinceLastReport === 'boolean') return o.closedImagesOnlySinceLastReport;
  if (erledigteNachAlterRegel(optionen, berichtsDatum)) return o?.hideClosedImagesAfterNextReport === true;
  return true;
};

/**
 * Wird dieser Bericht noch nach der ALTEN Regel gedruckt? Ja für einen
 * Bericht vor dem Stichtag ohne neuen Schlüssel — dann gilt nicht nur der
 * alte Schalter, sondern auch der alte Vergleich (Zeichenkette statt
 * Kalendertag, siehe `teileErledigteFuerPdf`): der Neudruck aus dem Archiv
 * muss Karte für Karte dem damals versendeten PDF entsprechen.
 */
export const erledigteNachAlterRegel = (
  optionen?: Partial<PrintOptions> | null,
  berichtsDatum?: string | null,
): boolean => {
  const o = optionen as Partial<BerichtDruckOptionen> | null | undefined;
  if (typeof o?.closedImagesOnlySinceLastReport === 'boolean') return false;
  const tag = berichtsDatum ? kalendertag(berichtsDatum) : '';
  return /^\d{4}-\d{2}-\d{2}$/.test(tag) && tag < ERLEDIGTE_OHNE_BILDER_STICHTAG;
};

/** Was vom Berichtsarchiv für die Frage „wann war der Vorbericht?" gebraucht wird. */
export interface BerichtsKopf {
  inspectionNumber: number;
  sectionId?: string | null;
  date: string;
}

/**
 * Datum des Vorberichts: der Archiveintrag mit der höchsten Nummer unterhalb
 * der aktuellen — bei Abschnitten nur aus demselben Abschnitt. Ohne
 * Vorbericht (erster Bericht) `null`: dann gilt alles Erledigte als „seit dem
 * Vorbericht" und behält seine Karte.
 */
export const vorberichtsDatum = (
  koepfe: readonly BerichtsKopf[] | null | undefined,
  inspectionNumber: number | undefined,
  sectionId: string | null | undefined
): string | null => {
  if (!koepfe || koepfe.length === 0) return null;
  const aktuelleNummer = inspectionNumber || 0;
  let vorbericht: BerichtsKopf | null = null;
  for (const kopf of koepfe) {
    if (sectionId && kopf.sectionId !== sectionId) continue;
    if (!(kopf.inspectionNumber < aktuelleNummer)) continue;
    // Bei gleicher Nummer gewinnt der zuerst gelistete — wie das frühere
    // stabile Sortieren mit anschließendem [0].
    if (!vorbericht || kopf.inspectionNumber > vorbericht.inspectionNumber) vorbericht = kopf;
  }
  return vorbericht?.date || null;
};

/**
 * Teilt die erledigten Mängel der Historie in Karten (mit Fotos und
 * Beweisbild) und reine Tabellenzeilen.
 *
 * Karte: Beweisbild vorhanden UND — bei `nurSeitVorbericht` mit bekanntem
 * Vorbericht — nach dessen Datum erledigt (oder ohne Erledigt-Datum, das
 * bleibt wie bisher sichtbar). Alles andere wird Tabellenzeile. Reihenfolge
 * bleibt die der Eingabe.
 */
export const teileErledigteFuerPdf = <T extends Pick<InspectionItem, 'closedImage' | 'closedDate'>>(
  erledigte: readonly T[],
  nurSeitVorbericht: boolean,
  vorberichtsTag: string | null,
  // Alte Regel (Archivbericht vor dem Stichtag): roher Zeichenkettenvergleich
  // wie bis 30.09.2026 — ein am Vorberichtstag mit Uhrzeit Erledigter lag
  // damals „danach“ und bekam seine Karte.
  alterVergleich = false
): { karten: T[]; tabelle: T[] } => {
  const karten: T[] = [];
  const tabelle: T[] = [];
  const grenze = vorberichtsTag ? (alterVergleich ? vorberichtsTag : kalendertag(vorberichtsTag)) : null;
  for (const item of erledigte) {
    const erledigtAm = item.closedDate ? (alterVergleich ? item.closedDate : kalendertag(item.closedDate)) : '';
    const aelter = nurSeitVorbericht && !!grenze && !!erledigtAm && erledigtAm <= grenze;
    if (item.closedImage && !aelter) karten.push(item);
    else tabelle.push(item);
  }
  return { karten, tabelle };
};

/** So viele Fotos zeigt eine Mangelkarte höchstens (eins rechts, zwei unten). */
export const PDF_KARTE_MAX_FOTOS = 3;

/**
 * Welche Bilder die Bildoptimierung vorbereiten muss — genau die, die auf
 * einer Karte gezeichnet werden, und nichts sonst. Früher liefen ALLE Fotos
 * aller Einträge durch die Optimierung, auch die der Erledigten, die nur als
 * Tabellenzeile erscheinen; bei 6051 über tausend Bilder, der Cache in
 * `cropAndCompressImage` lief dabei über und Vorschau wie Versand rechneten
 * jedes Mal alles neu.
 *
 * `karten`: alle Einträge, die als Karte gedruckt werden (aktuelle
 * Feststellungen und Historien-Karten). Pro Eintrag höchstens
 * `PDF_KARTE_MAX_FOTOS` Fotos, das Beweisbild nur bei Erledigten.
 */
export const bilderFuerPdf = <T extends Pick<InspectionItem, 'id' | 'status' | 'photos' | 'closedImage'>>(
  karten: readonly T[]
): { fotos: Array<{ id: string; quellen: string[] }>; beweisbilder: Array<{ id: string; quelle: string }> } => {
  const fotos: Array<{ id: string; quellen: string[] }> = [];
  const beweisbilder: Array<{ id: string; quelle: string }> = [];
  for (const item of karten) {
    if (item.photos && item.photos.length > 0) {
      fotos.push({ id: item.id, quellen: item.photos.slice(0, PDF_KARTE_MAX_FOTOS) });
    }
    if (item.status === 'closed' && item.closedImage) {
      beweisbilder.push({ id: item.id, quelle: item.closedImage });
    }
  }
  return { fotos, beweisbilder };
};

/**
 * Die Pläne, die tatsächlich eine Planseite bekommen: ausgewählt und mit
 * mindestens einer Nadel (Mangel nach `selectItemsOnPlan` oder
 * Sicherheitspunkt). Dient dem Zeichnen UND der Bildoptimierung — ein Plan
 * ohne Nadel wird nicht mehr teuer verkleinert, nur um dann wegzufallen.
 */
export const planeFuerPdf = <P extends { id: string }>(
  plans: readonly P[] | undefined,
  items: Array<StatusOnly & Pick<InspectionItem, 'planId' | 'planCoordinates'>>,
  safetyPoints: ReadonlyArray<Pick<SafetyPoint, 'planId'>> | undefined,
  optionen: Pick<PrintOptions, 'includePlans' | 'includedPlanIds'>
): P[] => {
  if (!optionen.includePlans || !plans || plans.length === 0) return [];
  return plans.filter(plan => {
    if (optionen.includedPlanIds && !optionen.includedPlanIds.includes(plan.id)) return false;
    // Derselbe Statusfilter wie beim Zeichnen und wie in der Bildschirm-
    // Planansicht: bleibt nach dem Filtern kein Punkt übrig, entfällt die
    // Planseite ganz — genau wie bei einem Plan ohne jede Markierung.
    return selectItemsOnPlan(items, plan.id).length > 0
      || (safetyPoints?.some(p => p.planId === plan.id) ?? false);
  });
};

export interface InspectionStats {
  total: number;
  open: number;
  closed: number;
  /** Status 'info' — im Bericht „Hinweis". */
  info: number;
  good: number;
  /** Status 'general' — im Bericht „Information". */
  general: number;
  /** Erledigt = closed + good, gleiche Zählweise wie in companyMergeLogic. */
  done: number;
  /** Was überhaupt abgearbeitet werden KANN: ohne Hinweis und ohne Information. */
  actionableTotal: number;
  progressPercent: number;
}

/**
 * Zählt die Einträge nach Status und leitet die Abarbeitungsquote ab.
 *
 * Hinweis UND Information sind Mitteilungen, nichts zum Abhaken. Beide fließen
 * deshalb nicht in `actionableTotal` ein — sonst drücken sie eine Quote, die
 * niemand jemals auf 100 % bringen könnte. Sie bleiben aber Teil von `total`
 * und bekommen einen eigenen Zähler, damit die Summe der Statuszeilen die
 * Gesamtzahl ergibt.
 */
export const countInspectionStats = (items: StatusOnly[]): InspectionStats => {
  const total = items.length;
  const open = items.filter(i => i.status === 'open').length;
  const closed = items.filter(i => i.status === 'closed').length;
  const info = items.filter(i => i.status === 'info').length;
  const good = items.filter(i => i.status === 'good').length;
  const general = items.filter(i => i.status === 'general').length;
  const done = closed + good;
  const actionableTotal = total - info - general;
  return {
    total, open, closed, info, good, general, done, actionableTotal,
    progressPercent: actionableTotal > 0 ? Math.round((done / actionableTotal) * 100) : 0
  };
};

export const sanitizeRole = (role: string, _lang: string) => {
    return role;
};

export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

const createPlaceholderImage = (w: number, h: number, text: string = 'Bild fehlt'): string => {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = '#f1f5f9'; // Slate-100
            ctx.fillRect(0, 0, w, h);
            ctx.strokeStyle = '#cbd5e1'; // Slate-300
            ctx.lineWidth = 4;
            ctx.strokeRect(0, 0, w, h);
            
            ctx.fillStyle = '#94a3b8'; // Slate-400
            ctx.font = 'bold 40px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(text, w / 2, h / 2);
            
            ctx.font = '20px sans-serif';
            ctx.fillText('(Datenbank-Fehler)', w / 2, h / 2 + 30);
        }
        return canvas.toDataURL('image/jpeg', 0.8);
    } catch {
        return '';
    }
};

// --- Bild-Optimierungs-Cache -------------------------------------------------
// Memoisiert die (teure) Decode+Canvas+Re-Encode-Operation pro Quellbild und
// Zielparametern. Dadurch wird die Bildoptimierung nur einmal pro Session/Bild
// gerechnet – z.B. Vorschau füllt den Cache, der Versand (EmailModal) nutzt ihn.
// LRU-begrenzt, damit der Speicher bei vielen verschiedenen Bildern nicht wächst.
const imgCache = new Map<string, string>();
const IMG_CACHE_MAX = 400;

// Günstiger Fingerprint statt des kompletten (riesigen) Base64-Strings als Key.
const imgCacheKey = (src: string, w: number, h: number, q: number) =>
    `${w}x${h}q${q}|${src.length}|${src.slice(0, 48)}|${src.slice(-48)}`;

const imgCacheGet = (key: string): string | undefined => {
    const v = imgCache.get(key);
    if (v !== undefined) {
        // Recency auffrischen (Map behält Einfügereihenfolge bei → LRU).
        imgCache.delete(key);
        imgCache.set(key, v);
    }
    return v;
};

const imgCacheSet = (key: string, val: string) => {
    imgCache.set(key, val);
    if (imgCache.size > IMG_CACHE_MAX) {
        const oldest = imgCache.keys().next().value;
        if (oldest !== undefined) imgCache.delete(oldest);
    }
};

export const cropAndCompressImage = (base64Str: string, targetWidth: number, targetHeight: number, quality: number): Promise<string> => {
    if (!base64Str || base64Str === 'placeholder') {
        return Promise.resolve(createPlaceholderImage(targetWidth, targetHeight));
    }

    const key = imgCacheKey(base64Str, targetWidth, targetHeight, quality);
    const cached = imgCacheGet(key);
    if (cached) return Promise.resolve(cached);

    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = base64Str;
        img.onload = () => {
            const srcW = img.width;
            const srcH = img.height;
            const scale = Math.min(targetWidth / srcW, targetHeight / srcH);
            const w = srcW * scale;
            const h = srcH * scale;
            const x = (targetWidth - w) / 2;
            const y = (targetHeight - h) / 2;
            const canvas = document.createElement('canvas');
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, targetWidth, targetHeight);
                ctx.drawImage(img, x, y, w, h);
                const out = canvas.toDataURL('image/jpeg', quality);
                imgCacheSet(key, out); // nur erfolgreiche Ergebnisse cachen
                resolve(out);
            } else {
                resolve(base64Str);
            }
        };
        // Fehler/Placeholder NICHT cachen, damit transiente Ladefehler nicht haften bleiben.
        img.onerror = () => resolve(createPlaceholderImage(targetWidth, targetHeight, 'Ladefehler'));
    });
};

/**
 * Plan fuer den PDF-Anhang verkleinern und als JPEG neu kodieren.
 *
 * `maxWidth` begrenzt die LANGE Kante (Name aus der Zeit, als Plaene nur quer
 * kamen): ein hochkant hochgeladener Plan blieb sonst in voller Hoehe im PDF
 * und blaehte es auf (docs/planverortung-vertrag.md P4: lange Kante ~2800 px).
 */
export const optimizePlanForPdf = (base64Str: string, maxWidth: number, quality: number): Promise<string> => {
    if (!base64Str || base64Str === 'placeholder') {
        return Promise.resolve(createPlaceholderImage(maxWidth, maxWidth * 0.75));
    }

    const key = imgCacheKey(base64Str, maxWidth, 0, quality);
    const cached = imgCacheGet(key);
    if (cached) return Promise.resolve(cached);

    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = base64Str;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let w = img.width;
            let h = img.height;
            const langeKante = Math.max(w, h);
            if (langeKante > maxWidth) {
                const faktor = maxWidth / langeKante;
                w = Math.round(w * faktor);
                h = Math.round(h * faktor);
            }
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0, w, h);
                const out = canvas.toDataURL('image/jpeg', quality);
                imgCacheSet(key, out); // nur erfolgreiche Ergebnisse cachen
                resolve(out);
            } else {
                resolve(base64Str);
            }
        };
        img.onerror = () => resolve(createPlaceholderImage(maxWidth, maxWidth * 0.75, 'Ladefehler'));
    });
};

/**
 * Foto fuer eine Mangelkarte verkleinern: lange Kante hoechstens `maxKante`,
 * Seitenverhaeltnis bleibt — weder beschnitten noch aufgefuellt. Anders als
 * cropAndCompressImage (passt in ein festes Feld und fuellt den Rest weiss)
 * bleibt ein Hochformatfoto hochkant; das Layout richtet sich nach dem Bild.
 * Eigener Name, damit Tests Fotos und Plaene getrennt beobachten koennen.
 */
export const optimizePhotoForPdf = (base64Str: string, maxKante: number, quality: number): Promise<string> =>
    optimizePlanForPdf(base64Str, maxKante, quality);

export const formatDate = (dateStr: string | undefined | null) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
  });
};

/**
 * Blickpfeil am Plan-Pin (Sensor-Verortung, docs/verortung-kamera-vertrag.md):
 * Spitze in `laenge` mm Entfernung in Richtung `gradPlan` (im Uhrzeigersinn ab
 * Plan-oben; PDF-y waechst nach unten) und die beiden Ecken der Pfeilspitze.
 */
export const blickpfeil = (x: number, y: number, gradPlan: number, laenge: number) => {
  const rad = (gradPlan * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const spitze = { x: x + dx * laenge, y: y + dy * laenge };
  const basis = { x: spitze.x - dx * 2.2, y: spitze.y - dy * 2.2 };
  // Senkrechte zur Richtung fuer die Breite der Spitze.
  const px = -dy * 1.2;
  const py = dx * 1.2;
  return {
    spitze,
    links: { x: basis.x + px, y: basis.y + py },
    rechts: { x: basis.x - px, y: basis.y - py },
  };
};

// --- Stecknadeln auf dem Plan (docs/planverortung-vertrag.md P4) ------------
// Betreiber 28.09.2026: „wie eine echte Pinnnadel“ — glaenzende rote Kugel auf
// duennem Metallstift, leicht schraeg gesteckt, Licht von oben links, weicher
// Schatten auf dem Papier rechts unten; stehen mehrere Nadeln beieinander,
// faechern sie auf (Neigungen: services/planNadelNeigung `nadelNeigungen`).
// Gleiche Proportionen wie die Bildschirm-Nadel (components/plaene/PlanNadel.tsx:
// Kugel R, Stift L = 2,3·R, Schatten um 0,75·R | 0,9·R gegen die Kugel versetzt).

/** Kugelradius im PDF (mm). */
export const STECKNADEL_KUGEL_MM = 2.7;
/** Stiftlaenge L (Spitze → Kugelmitte) in Kugelradien. */
export const STECKNADEL_STIFT_FAKTOR = 2.3;
/** Spitzenabstand in Kugelradien, unter dem Nadeln als Gruppe aufgefaechert werden. */
export const STECKNADEL_GRUPPEN_FAKTOR = 2.2;
/** Versatz der Schattenmitte gegen die Kugelmitte in Kugelradien (Licht oben links). */
export const STECKNADEL_SCHATTEN_VERSATZ = { x: 0.75, y: 0.9 } as const;
/** Staerke des Metallstifts (mm). */
export const STECKNADEL_STIFT_MM = 0.45;
/** Rand um die Kugel (Kontur + Luft), der fuer Ueberstand und Verdeckung mitzaehlt (mm). */
export const STECKNADEL_SAUM_MM = 0.2;
/** Deckkraft des Kugelschattens in der Mitte (Summe aller Lagen). */
export const STECKNADEL_SCHATTEN_DECKKRAFT = 0.25;
/** Groesse der Schattenlagen gegen die Schattenellipse (aussen → innen): weicher Rand. */
const STECKNADEL_SCHATTEN_LAGEN = [1.35, 1.2, 1.05, 0.9, 0.75, 0.6] as const;
/** Strichstaerke des Blickpfeils (mm). */
export const BLICKPFEIL_STRICH_MM = 0.6;

export interface NadelPunkt { x: number; y: number }

export interface StecknadelGeometrie {
  /** Genau die Plankoordinate: hier steckt die Nadel im Papier. */
  spitze: NadelPunkt;
  kugel: NadelPunkt & { r: number };
  /** Kugelschatten auf dem Papier (Ellipse), rechts unterhalb der Kugel. */
  schatten: NadelPunkt & { rx: number; ry: number };
  /** Tatsaechliche Neigung (Grad), auf ±NADEL_MAX_NEIGUNG begrenzt. */
  neigung: number;
}

/** Neigung begrenzen; keine endliche Zahl → Grundneigung. */
const neigungBegrenzt = (grad: number): number =>
  Number.isFinite(grad) ? Math.max(-NADEL_MAX_NEIGUNG, Math.min(NADEL_MAX_NEIGUNG, grad)) : NADEL_GRUNDNEIGUNG;

/**
 * Geometrie einer Stecknadel mit der Spitze auf (x, y). Die Kugel sitzt am
 * Stiftende: Kugelmitte = Spitze + L·(sin θ, −cos θ), L = 2,3·r, θ = Neigung
 * (0 = senkrecht, + = Kugel nach rechts; PDF-y waechst nach unten). Der
 * Schatten liegt rechts unterhalb der Kugel.
 */
export const stecknadelGeometrie = (x: number, y: number, kugelRadius: number, neigungGrad: number): StecknadelGeometrie => {
  const r = Math.max(0, kugelRadius);
  const neigung = neigungBegrenzt(neigungGrad);
  const bogen = (neigung * Math.PI) / 180;
  const l = STECKNADEL_STIFT_FAKTOR * r;
  const kugel = { x: x + l * Math.sin(bogen), y: y - l * Math.cos(bogen), r };
  return {
    spitze: { x, y },
    kugel,
    schatten: {
      x: kugel.x + STECKNADEL_SCHATTEN_VERSATZ.x * r,
      y: kugel.y + STECKNADEL_SCHATTEN_VERSATZ.y * r,
      rx: 1.05 * r,
      ry: 0.8 * r,
    },
    neigung,
  };
};

/**
 * RGB mit Weiss (anteil > 0) oder Schwarz (anteil < 0) mischen, |anteil| 0–1
 * — hellere und dunklere Toene derselben Kugelfarbe.
 */
export const nadelFarbeMischen = (farbe: readonly number[], anteil: number): [number, number, number] => {
  const a = Number.isFinite(anteil) ? Math.max(-1, Math.min(1, anteil)) : 0;
  const ziel = a >= 0 ? 255 : 0;
  const kanal = (i: number): number => {
    const wert = Number.isFinite(farbe[i]) ? Math.max(0, Math.min(255, farbe[i])) : 0;
    return Math.round(wert + (ziel - wert) * Math.abs(a));
  };
  return [kanal(0), kanal(1), kanal(2)];
};

/** Schriftgroesse (pt) der Nummer in der Kugel: bis 3 Stellen passen in r ≈ 2,7 mm. */
export const nadelSchriftgroesse = (text: string): number =>
  text.length <= 2 ? 7 : (text.length === 3 ? 6 : 5);

/**
 * Laenge des Blickpfeils an einer Stecknadel. Der Pfeil beginnt an der Spitze
 * und liegt UNTER der Nadel; verdeckt wird er nur, wo der Strahl die Kugel
 * (samt Saum und halber Strichbreite) schneidet — der Stift ist schmaler als
 * der Pfeil. Um diese Strecke wird er verlaengert, damit immer `sichtbar` mm
 * herausragen.
 */
export const blickpfeilLaengeAnNadel = (
  gradPlan: number,
  kugelRadius: number,
  neigungGrad: number,
  sichtbar = 7,
): number => {
  const r = Math.max(0, kugelRadius);
  const huelle = r + STECKNADEL_SAUM_MM + BLICKPFEIL_STRICH_MM / 2;
  const { kugel } = stecknadelGeometrie(0, 0, r, neigungGrad);
  const rad = (gradPlan * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  // Strahl–Kreis-Schnitt: Lotfusspunkt der Kugelmitte auf dem Strahl, dann
  // bis zum Austritt aus der Kugel.
  const lot = kugel.x * dx + kugel.y * dy;
  const rest = huelle * huelle - (kugel.x * kugel.x + kugel.y * kugel.y - lot * lot);
  const verdeckt = rest > 0 ? Math.max(0, lot + Math.sqrt(rest)) : 0;
  return verdeckt + sichtbar;
};

/**
 * Das Stueck jsPDF, das die Stecknadel braucht — klein gehalten, damit ein
 * Test die Aufrufe mit einem Fake pruefen kann. Transparenz (`setGState` +
 * die Klasse `GState` am Dokument) ist optional: ohne sie entfaellt der
 * Schatten (deckend wuerde er den Plan verdecken).
 */
export interface NadelZeichenflaeche {
  setFillColor(r: number, g: number, b: number): unknown;
  setDrawColor(r: number, g: number, b: number): unknown;
  setLineWidth(width: number): unknown;
  setTextColor(r: number, g: number, b: number): unknown;
  setFont(fontName: string, fontStyle?: string): unknown;
  setFontSize(size: number): unknown;
  getTextWidth(text: string): number;
  line(x1: number, y1: number, x2: number, y2: number, style?: string | null): unknown;
  triangle(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, style?: string | null): unknown;
  circle(x: number, y: number, r: number, style?: string | null): unknown;
  ellipse(x: number, y: number, rx: number, ry: number, style?: string | null): unknown;
  roundedRect(x: number, y: number, w: number, h: number, rx: number, ry: number, style?: string | null): unknown;
  text(text: string, x: number, y: number, options?: { align?: 'left' | 'center' | 'right'; baseline?: 'middle' }): unknown;
  setGState?(gState: unknown): unknown;
  /** jsPDF: `new doc.GState({ opacity })` (Plugin, fehlt in den Typen als Konstruktor). */
  GState?: unknown;
}

/**
 * Deckkraft fuer Flaechen und Linien setzen (0–1). false, wenn das Dokument
 * keine Transparenz kann.
 */
const setzeDeckkraft = (doc: NadelZeichenflaeche, deckkraft: number): boolean => {
  const Klasse = doc.GState;
  if (!doc.setGState || typeof Klasse !== 'function') return false;
  doc.setGState(Reflect.construct(Klasse, [{ opacity: deckkraft, 'stroke-opacity': deckkraft }]));
  return true;
};

/**
 * `zeichnen` mit Deckkraft ausfuehren; danach steht sie IMMER wieder auf 1 —
 * auch wenn ein Zeichenaufruf wirft (der Generator faengt Fehler je Plan ab
 * und zeichnet weiter; sonst waere alles Folgende halbdurchsichtig,
 * Codex-Review 28.09.). Ohne Transparenz wird nichts gezeichnet → false.
 */
const mitDeckkraft = (doc: NadelZeichenflaeche, deckkraft: number, zeichnen: () => void): boolean => {
  if (!setzeDeckkraft(doc, deckkraft)) return false;
  try {
    zeichnen();
  } finally {
    setzeDeckkraft(doc, 1);
  }
  return true;
};

/**
 * Schatten einer Stecknadel aufs Papier: der Stift als schmaler Streif von der
 * Spitze zur Schattenmitte, die Kugel als weiche Ellipse (drei Lagen, innen
 * dunkler). Vor ALLEN Nadeln zeichnen, damit kein Schatten auf einer Kugel
 * liegt. Danach steht die Deckkraft wieder auf 1.
 */
export const zeichneStecknadelSchatten = (doc: NadelZeichenflaeche, geo: StecknadelGeometrie): void => {
  const { spitze, schatten } = geo;
  mitDeckkraft(doc, 0.13, () => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(STECKNADEL_STIFT_MM * 0.8);
    doc.line(spitze.x, spitze.y, schatten.x, schatten.y);
    // Weicher Rand ohne Unschaerfe: gleich blasse Lagen, nach innen kleiner —
    // sie addieren sich zur Mitte hin auf etwa STECKNADEL_SCHATTEN_DECKKRAFT.
    const lage = 1 - Math.pow(1 - STECKNADEL_SCHATTEN_DECKKRAFT, 1 / STECKNADEL_SCHATTEN_LAGEN.length);
    setzeDeckkraft(doc, lage);
    doc.setFillColor(0, 0, 0);
    for (const faktor of STECKNADEL_SCHATTEN_LAGEN) {
      doc.ellipse(schatten.x, schatten.y, schatten.rx * faktor, schatten.ry * faktor, 'F');
    }
  });
};

/**
 * Stecknadel ins PDF zeichnen (ohne Schatten, siehe zeichneStecknadelSchatten):
 * Einstich an der Spitze, Metallstift mit Glanzlinie, glaenzende Kugel
 * (Farbverlauf in Stufen: dunkler Rand rechts unten, hell oben links, weisses
 * Glanzlicht), duenne dunkle Kontur, Nummer weiss und fett mittig.
 * `beschriftung` (Sicherheitspunkte) steht als kleiner weisser Kasten mit
 * duennem Rand ueber der Kugel.
 */
export const zeichneStecknadel = (
  doc: NadelZeichenflaeche,
  x: number,
  y: number,
  kugelRadius: number,
  farbe: readonly number[],
  nummer?: string | number | null,
  beschriftung?: string | null,
  neigungGrad: number = NADEL_GRUNDNEIGUNG,
): StecknadelGeometrie => {
  const g = stecknadelGeometrie(x, y, kugelRadius, neigungGrad);
  const { kugel: k } = g;
  const r = k.r;
  const bogen = (g.neigung * Math.PI) / 180;
  const richtung = { x: Math.sin(bogen), y: -Math.cos(bogen) };
  // Quer zum Stift, zur rechten (lichtabgewandten) Seite.
  const quer = { x: Math.cos(bogen), y: Math.sin(bogen) };
  const neben = (p: NadelPunkt, abstand: number): NadelPunkt => ({ x: p.x + quer.x * abstand, y: p.y + quer.y * abstand });

  // Einstich: kleines dunkles Loch im Papier.
  const einstich = () => {
    doc.setFillColor(15, 23, 42);
    doc.circle(x, y, 0.3, 'F');
  };
  if (!mitDeckkraft(doc, 0.6, einstich)) einstich();

  // Metallstift: kurze Spitze, dann volle Staerke bis unter die Kugel.
  const w = STECKNADEL_STIFT_MM / 2;
  const ansatz = { x: x + richtung.x * 0.45 * r, y: y + richtung.y * 0.45 * r };
  const ansatzRechts = neben(ansatz, w);
  const ansatzLinks = neben(ansatz, -w);
  doc.setFillColor(148, 163, 184);
  doc.triangle(x, y, ansatzRechts.x, ansatzRechts.y, ansatzLinks.x, ansatzLinks.y, 'F');
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(STECKNADEL_STIFT_MM);
  doc.line(ansatz.x, ansatz.y, k.x, k.y);
  // Schattenkante rechts, Glanzlinie links.
  const kanteA = neben(ansatz, w * 0.7);
  const kanteB = neben(k, w * 0.7);
  doc.setDrawColor(71, 85, 105);
  doc.setLineWidth(0.12);
  doc.line(kanteA.x, kanteA.y, kanteB.x, kanteB.y);
  const glanzA = neben(ansatz, -w * 0.4);
  const glanzB = neben(k, -w * 0.4);
  doc.setDrawColor(248, 250, 252);
  doc.setLineWidth(0.1);
  doc.line(glanzA.x, glanzA.y, glanzB.x, glanzB.y);

  // Kugel: Farbverlauf in Stufen. Jede Stufe ein kleinerer, hellerer Kreis,
  // Richtung Licht (oben links) versetzt — jeder liegt ganz im vorigen, so
  // bleibt rechts unten ein dunkler Rand.
  const toene: Array<[number, [number, number, number]]> = [
    [0, nadelFarbeMischen(farbe, -0.45)],
    [0.3, nadelFarbeMischen(farbe, 0)],
    [0.7, nadelFarbeMischen(farbe, 0.2)],
    [1, nadelFarbeMischen(farbe, 0.5)],
  ];
  const tonBei = (t: number): [number, number, number] => {
    for (let i = 1; i < toene.length; i++) {
      const [t1, f1] = toene[i];
      const [t0, f0] = toene[i - 1];
      if (t <= t1) {
        const u = (t - t0) / (t1 - t0);
        return [0, 1, 2].map(j => Math.round(f0[j] + (f1[j] - f0[j]) * u)) as [number, number, number];
      }
    }
    return toene[toene.length - 1][1];
  };
  const STUFEN = 12;
  const licht = { x: -0.3 * r, y: -0.34 * r };
  for (let i = 0; i < STUFEN; i++) {
    const t = i / (STUFEN - 1);
    const ton = tonBei(t);
    doc.setFillColor(ton[0], ton[1], ton[2]);
    doc.circle(k.x + licht.x * t, k.y + licht.y * t, r * (1 - 0.78 * t), 'F');
  }
  // Glanzlicht oben links: weich (halbdurchsichtig), mit kleinem hellen Kern.
  mitDeckkraft(doc, 0.6, () => {
    doc.setFillColor(255, 255, 255);
    doc.ellipse(k.x - 0.44 * r, k.y - 0.5 * r, 0.28 * r, 0.18 * r, 'F');
    setzeDeckkraft(doc, 0.9);
    doc.circle(k.x - 0.46 * r, k.y - 0.52 * r, 0.09 * r, 'F');
  });
  const rand = nadelFarbeMischen(farbe, -0.62);
  doc.setDrawColor(rand[0], rand[1], rand[2]);
  doc.setLineWidth(0.12);
  doc.circle(k.x, k.y, r, 'S');

  const text = nummer === null || nummer === undefined ? '' : String(nummer);
  if (text) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(nadelSchriftgroesse(text));
    doc.setTextColor(255, 255, 255);
    doc.text(text, k.x, k.y, { align: 'center', baseline: 'middle' });
  }

  if (beschriftung) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    const breite = doc.getTextWidth(beschriftung) + 2;
    const hoehe = 4;
    const unten = k.y - r - 0.6;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(51, 65, 85);
    doc.setLineWidth(0.1);
    doc.roundedRect(k.x - breite / 2, unten - hoehe, breite, hoehe, 1, 1, 'FD');
    doc.setTextColor(0, 0, 0);
    doc.text(beschriftung, k.x, unten - hoehe / 2, { align: 'center', baseline: 'middle' });
  }
  return g;
};

// --- Planseite A4 quer -------------------------------------------------------

/** Linie unter der laufenden Kopfzeile des Begehungsprotokolls (mm von oben). */
export const KOPFZEILE_LINIE_Y = 16;

/**
 * Masse der Planseite (mm). Kopflinie der laufenden Kopfzeile bei 16
 * (KOPFZEILE_LINIE_Y); Titelzeile „Verortung: …“ (11 pt, Unterlaenge bis ~27)
 * darunter; die Fusslinie steht bei H−13, Seitenzahl und Fusszeile darunter —
 * die Bildunterkante bleibt deshalb 16 mm ueber dem Blattrand.
 */
export const PLAN_SEITE = { titelY: 25, bildOben: 28.5, untenFrei: 16 } as const;

/**
 * Groesste Box fuer das Planbild: seitlich `rand`, oben unter der Titelzeile,
 * unten Platz fuer die Fusszeile (`untenFrei`, mehr bei mehrzeiliger
 * Fusszeile); Seitenverhaeltnis bleibt, zentriert. Ohne gueltige Masse → null.
 */
export const planBildRahmen = (
  seiteBreite: number,
  seiteHoehe: number,
  bildBreite: number,
  bildHoehe: number,
  rand: number,
  bildOben: number = PLAN_SEITE.bildOben,
  untenFrei: number = PLAN_SEITE.untenFrei,
): { x: number; y: number; w: number; h: number } | null => {
  const boxW = seiteBreite - 2 * rand;
  const boxH = seiteHoehe - untenFrei - bildOben;
  if (!(bildBreite > 0) || !(bildHoehe > 0) || boxW <= 0 || boxH <= 0) return null;
  const skala = Math.min(boxW / bildBreite, boxH / bildHoehe);
  const w = bildBreite * skala;
  const h = bildHoehe * skala;
  return { x: rand + (boxW - w) / 2, y: bildOben + (boxH - h) / 2, w, h };
};

/** Unterkante der Titelzeile „Verortung: …“ (11 pt, Unterlaengen) + Luft. */
export const PLAN_TITEL_UNTERKANTE = PLAN_SEITE.titelY + 1.5;

/**
 * Wie weit ragt eine Stecknadel ueber ihre Spitze nach oben (mm)? Stift bis
 * zur Kugelmitte (L·cos θ), Kugel samt Saum, optional die Beschriftung
 * darueber (zeichneStecknadel) und ein nach oben zeigender Blickpfeil (Laenge
 * wie im Generator). `neigungGrad` null = Obergrenze fuer JEDE Neigung: die
 * senkrechte Nadel steht am hoechsten, und der Blickpfeil wird hoechstens um
 * Stift plus Kugeldurchmesser verlaengert.
 */
export const nadelUeberstandOben = (
  kugelRadius: number,
  mitBeschriftung: boolean,
  neigungGrad: number | null,
  blickGradPlan?: number | null,
  blickSichtbar = 7,
): number => {
  const r = Math.max(0, kugelRadius);
  const l = STECKNADEL_STIFT_FAKTOR * r;
  const hoehe = neigungGrad === null ? l : l * Math.cos((neigungBegrenzt(neigungGrad) * Math.PI) / 180);
  let oben = hoehe + r + STECKNADEL_SAUM_MM;
  if (mitBeschriftung) oben += 0.6 + 4;
  if (typeof blickGradPlan === 'number' && Number.isFinite(blickGradPlan)) {
    const laenge = neigungGrad === null
      ? l + r + STECKNADEL_SAUM_MM + BLICKPFEIL_STRICH_MM / 2 + blickSichtbar
      : blickpfeilLaengeAnNadel(blickGradPlan, r, neigungGrad, blickSichtbar);
    oben = Math.max(oben, Math.cos((blickGradPlan * Math.PI) / 180) * laenge + BLICKPFEIL_STRICH_MM);
  }
  return oben;
};

/**
 * Wie `planBildRahmen`, aber keine Nadel (samt Beschriftung/Blickpfeil) ragt
 * in die Titelzeile (Codex-Review 28.09., P2): steht eine Nadel nah am oberen
 * Planrand, rueckt das Bild so weit nach unten (und wird dabei kleiner), wie
 * noetig. Ohne solche Nadeln bleibt die Box unveraendert. `elemente`: je
 * Nadel die Hoehe der Spitze in Plan-Prozent und ihr Ueberstand in mm.
 */
export const planBildRahmenMitNadeln = (
  seiteBreite: number,
  seiteHoehe: number,
  bildBreite: number,
  bildHoehe: number,
  rand: number,
  elemente: ReadonlyArray<{ yProzent: number; ueberstand: number }>,
  untenFrei: number = PLAN_SEITE.untenFrei,
): { x: number; y: number; w: number; h: number } | null => {
  const erster = planBildRahmen(seiteBreite, seiteHoehe, bildBreite, bildHoehe, rand, PLAN_SEITE.bildOben, untenFrei);
  if (!erster || elemente.length === 0) return erster;
  let oben: number = PLAN_SEITE.bildOben;
  let rahmen = erster;
  // Mehrere Runden: ein breitenbegrenztes Bild steht mittig in der Box und rueckt je Runde nur halb nach.
  for (let runde = 0; runde < 20; runde++) {
    const aktuell = rahmen;
    const hoechster = Math.min(...elemente.map(e => aktuell.y + (e.yProzent / 100) * aktuell.h - e.ueberstand));
    const fehlt = PLAN_TITEL_UNTERKANTE - hoechster;
    if (fehlt <= 0.01) break;
    oben += fehlt;
    const naechster = planBildRahmen(seiteBreite, seiteHoehe, bildBreite, bildHoehe, rand, oben, untenFrei);
    // Kein Platz mehr (absurd hohe Beschriftung o. ae.): lieber der letzte brauchbare Rahmen.
    if (!naechster || naechster.h < erster.h / 2) break;
    rahmen = naechster;
  }
  return rahmen;
};
